import { Ease, type Ease as EaseFn } from './Ease';
import { motion } from './Motion';

type Target = Record<string, unknown>;

export interface TweenOptions {
  ease?: EaseFn;
  delay?: number;
  /** Decorative tweens jump to the end when reduced motion is on. */
  decorative?: boolean;
  onUpdate?: (t: number) => void;
}

class Tween {
  target: Target = {};
  keys: string[] = [];
  from: number[] = [];
  to: number[] = [];
  duration = 0;
  elapsed = 0;
  delay = 0;
  ease: EaseFn = Ease.linear;
  onUpdate: ((t: number) => void) | undefined;
  resolve: (() => void) | undefined;
  started = false;
  alive = false;
}

/**
 * Pooled property tweens advanced by the fixed-step loop. Starting a tween
 * allocates nothing per frame; finished tweens return to the pool.
 */
export class Tweens {
  private active: Tween[] = [];
  private pool: Tween[] = [];

  to<T extends object>(target: T, props: Partial<Record<keyof T, number>>, ms: number, opts: TweenOptions = {}): Promise<void> {
    const tw = this.pool.pop() ?? new Tween();
    tw.target = target as unknown as Target;
    tw.keys.length = 0;
    tw.from.length = 0;
    tw.to.length = 0;
    for (const key of Object.keys(props)) {
      tw.keys.push(key);
      tw.to.push((props as Record<string, number>)[key] ?? 0);
    }
    const instant = opts.decorative && motion.reduced;
    tw.duration = instant ? 0 : Math.max(0, ms);
    tw.delay = instant ? 0 : (opts.delay ?? 0);
    tw.elapsed = 0;
    tw.ease = opts.ease ?? Ease.outQuad;
    tw.onUpdate = opts.onUpdate;
    tw.started = false;
    tw.alive = true;
    const done = new Promise<void>((r) => (tw.resolve = r));
    this.active.push(tw);
    return done;
  }

  /** Resolve after `ms` of game time (pauses with the loop). */
  wait(ms: number): Promise<void> {
    return this.to({ v: 0 }, { v: 1 }, ms, { ease: Ease.linear });
  }

  /** Stop all tweens on a target (their promises resolve immediately). */
  kill(target: object): void {
    for (const tw of this.active) if (tw.target === target) tw.alive = false;
  }

  update(dt: number): void {
    const list = this.active;
    let write = 0;
    for (let i = 0; i < list.length; i++) {
      const tw = list[i]!;
      // Display objects destroyed with their scene stop animating.
      if (tw.alive && (tw.target as { destroyed?: boolean }).destroyed) tw.alive = false;
      if (tw.alive) {
        if (tw.delay > 0) {
          tw.delay -= dt;
        } else {
          if (!tw.started) {
            tw.started = true;
            for (let k = 0; k < tw.keys.length; k++) tw.from[k] = Number(tw.target[tw.keys[k]!] ?? 0);
          }
          tw.elapsed += dt;
          const t = tw.duration <= 0 ? 1 : Math.min(1, tw.elapsed / tw.duration);
          const e = tw.ease(t);
          for (let k = 0; k < tw.keys.length; k++) {
            const from = tw.from[k]!;
            tw.target[tw.keys[k]!] = from + (tw.to[k]! - from) * e;
          }
          tw.onUpdate?.(t);
          if (t >= 1) tw.alive = false;
        }
      }
      if (tw.alive) {
        list[write++] = tw;
      } else {
        tw.resolve?.();
        tw.resolve = undefined;
        tw.onUpdate = undefined;
        tw.target = {};
        this.pool.push(tw);
      }
    }
    list.length = write;
  }

  get count(): number {
    return this.active.length;
  }

  clear(): void {
    for (const tw of this.active) tw.alive = false;
    this.update(0);
  }
}
